import {BulkAssemblyMethod} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {ValidationStatus} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import {autopopulateConstructsActionFunction} from 'molbio/bulk-assembly/tables/action-functions/autopopulateConstructsActionFunction';
import {deleteConstructsActionFunction} from 'molbio/bulk-assembly/tables/action-functions/deleteActionFunctions';
import {statusFilterConstructsActionFunction} from 'molbio/bulk-assembly/tables/action-functions/statusFilterActionFunction';
import {toggleFragmentDetailsActionFunction} from 'molbio/bulk-assembly/tables/action-functions/toggleFragmentDetailsActionFunction';
import {toggleOverlapDetailsActionFunction} from 'molbio/bulk-assembly/tables/action-functions/toggleOverlapDetailsActionFunction';
import {
  viewConstructsAxisContextMenuActionFunction,
  viewConstructsActionFunction,
} from 'molbio/bulk-assembly/tables/action-functions/viewActionFunctions';
import {downloadCSVActionFunction} from 'notebook/structured-tables/action-functions/downloadCSVActionFunction';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  ActionFunctions,
  OverflowMenuActionFunction,
  StructuredTable,
  StructuredTableColumn,
  StructuredTableSchema,
} from 'notebook/structured-tables/StructuredTableConstants';
import {Table, TableDimension, TableReorderLocks} from 'notebook/tables/TableConstants';

export interface BulkAssemblyConstructsStructuredTableMetadata {
  columns: Array<StructuredTableColumn>;
  onAutopopulate: () => void;
  onViewConstructs: (constructCids: Array<string>) => void;
  autopopulateButtonError: string | null;
  isFilterDisabledByStatus: {[key in ValidationStatus]: boolean};
  onDeleteAll: () => void;
  onDeleteFiltered: () => void;
  onDeleteRange: (startIndex: number, endIndex: number) => void;
  isFiltered: boolean;
  isReadOnly: boolean;
  bulkAssemblyMethod: BulkAssemblyMethod;
  showFragmentDetails: boolean;
  onToggleFragmentDetails: (showFragmentDetails: boolean) => void;
  showOverlapDetails: boolean;
  onToggleOverlapDetails: (showOverlapDetails: boolean) => void;
  statusFilter: ValidationStatus | null;
  onSetStatusFilter: (newStatusFilter: ValidationStatus | null) => void;
  tableRowsName: string;
}

export interface BulkAssemblyConstructsStructuredTableRowMetadata {
  constructCid: string | null;
  canPreview: boolean;
}

export type BulkAssemblyConstructsStructuredTable = StructuredTable<
  BulkAssemblyConstructsStructuredTableMetadata,
  BulkAssemblyConstructsStructuredTableRowMetadata
>;

/**
 * Structured table adapter describing the behavior of the constructs table in bulk assembly.
 *
 * This table is never used in the notebook, and has cell values controlled by the surrounding assembly code
 * as part of the overall assembly UI.
 */
export default class BulkAssemblyConstructsStructuredTableAdapter extends GenericStructuredTableAdapter<
  BulkAssemblyConstructsStructuredTableMetadata,
  BulkAssemblyConstructsStructuredTableRowMetadata
> {
  static getAdapter(): BulkAssemblyConstructsStructuredTableAdapter {
    return getStructuredTableAdapters()
      .BULK_ASSEMBLY_CONSTRUCTS as BulkAssemblyConstructsStructuredTableAdapter;
  }

  getActionFunctions(
    tableMetadata: BulkAssemblyConstructsStructuredTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<
    BulkAssemblyConstructsStructuredTableMetadata,
    BulkAssemblyConstructsStructuredTableRowMetadata
  > {
    const overflowActionFunctions: Array<
      OverflowMenuActionFunction<
        BulkAssemblyConstructsStructuredTableMetadata,
        BulkAssemblyConstructsStructuredTableRowMetadata
      >
    > = [downloadCSVActionFunction];

    if (tableMetadata.bulkAssemblyMethod === BulkAssemblyMethod.HOMOLOGY) {
      overflowActionFunctions.push(toggleFragmentDetailsActionFunction, toggleOverlapDetailsActionFunction);
    } else if (tableMetadata.bulkAssemblyMethod === BulkAssemblyMethod.GIBSON) {
      overflowActionFunctions.push(toggleOverlapDetailsActionFunction);
    }

    return {
      overflow: overflowActionFunctions,
      secondary: [
        statusFilterConstructsActionFunction,
        viewConstructsActionFunction,
        deleteConstructsActionFunction,
        autopopulateConstructsActionFunction,
      ],
      contextMenu: [viewConstructsAxisContextMenuActionFunction],
    };
  }

  getBlankRowMetadata(
    tableMetadata: BulkAssemblyConstructsStructuredTableMetadata
  ): BulkAssemblyConstructsStructuredTableRowMetadata {
    return {constructCid: null, canPreview: false};
  }

  getDisplayName(structuredTable: BulkAssemblyConstructsStructuredTable): string {
    return 'Constructs';
  }

  _getAllSupportedColumns({
    metadata,
  }: {
    metadata: BulkAssemblyConstructsStructuredTableMetadata;
  }): Array<StructuredTableColumn> {
    // Columns are dynamically decided at render time, so specify them as table metadata rather
    // than defining statically.
    return metadata.columns;
  }

  toTable(constructsTable: BulkAssemblyConstructsStructuredTable): Table {
    const table = super.toTable(constructsTable);
    return {...table, allowColumnEdits: false, lockedDims: true, allowNoRows: true};
  }

  allowReorderDimensions(): TableReorderLocks {
    return {
      [TableDimension.ROW]: false,
      [TableDimension.COLUMN]: false,
    };
  }
}
